// src/store/slices/authSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { User } from 'firebase/auth';
import { 
  loginWithEmailPassword, 
  resetPassword, 
  signOut,
  AuthResponse 
} from '../../services/firebase/auth';

// Types
interface AuthState {
  user: User | null;
  isAdmin: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// Initial state
const initialState: AuthState = {
  user: null,
  isAdmin: false,
  isAuthenticated: false,
  isLoading: false,
  error: null
};

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    const response: AuthResponse = await loginWithEmailPassword(email, password);
    if (!response.success) {
      return rejectWithValue(response.error);
    }
    return {
      user: response.user,
      isAdmin: response.isAdmin || false
    };
  }
);

export const sendPasswordReset = createAsyncThunk(
  'auth/resetPassword',
  async (email: string, { rejectWithValue }) => {
    const response: AuthResponse = await resetPassword(email);
    if (!response.success) {
      return rejectWithValue(response.error);
    }
    return true;
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    const response: AuthResponse = await signOut();
    if (!response.success) {
      return rejectWithValue(response.error);
    }
    return true;
  }
);

export interface SetUserPayload {
  user: User | null;
  isAdmin?: boolean;
}

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User | SetUserPayload | null>) => {
      const payload = action.payload;
      if (payload && 'user' in payload) {
        state.user = payload.user;
        state.isAdmin = payload.isAdmin || false;
        state.isAuthenticated = !!payload.user;
      } else {
        state.user = payload as User | null;
        state.isAdmin = false;
        state.isAuthenticated = !!payload;
      }
    },
    setAdmin: (state, action: PayloadAction<boolean>) => {
      state.isAdmin = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user || null;
        state.isAdmin = action.payload.isAdmin || false;
        state.isAuthenticated = !!action.payload.user;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Password reset
      .addCase(sendPasswordReset.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(sendPasswordReset.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(sendPasswordReset.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Logout
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.isAdmin = false;
        state.isAuthenticated = false;
      })
      .addCase(logout.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  }
});

export const { setUser, setAdmin, clearError } = authSlice.actions;
export default authSlice.reducer;